import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add token + session id to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  try {
    const sid = sessionStorage.getItem('devisgo_session_id');
    if (sid) config.headers['X-Session-Id'] = sid;
  } catch (e) { /* ignore */ }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Contacts
export const contactsAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getOne: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  // Activity timeline (phone calls, notes, etc.)
  listActivities: (id) => api.get(`/contacts/${id}/activities`),
  createActivity: (id, data) => api.post(`/contacts/${id}/activities`, data),
  deleteActivity: (id, activityId) => api.delete(`/contacts/${id}/activities/${activityId}`),
  summarizeCall: (data) => api.post('/contacts/call-debrief/summarize', data),
};

// Trips audit
export const tripsAPI = {
  start: (data) => api.post('/trips/start', data),
  end: (id, data) => api.post(`/trips/${id}/end`, data),
  byTask: (taskId) => api.get(`/trips/by-task/${taskId}`),
  remove: (id) => api.delete(`/trips/${id}`),
  snapshotUrl: (id) => `${API_URL}/api/trips/${id}/snapshot.png`,
};

// KBO / BCE (Banque-Carrefour des Entreprises)
export const kboAPI = {
  status: () => api.get('/kbo/status'),
  egressIp: () => api.get('/kbo/egress-ip'),
  sftpCheck: () => api.get('/kbo/sftp-check'),
  sync: () => api.post('/kbo/sync'),
  upload: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/kbo/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  search: (q, limit = 15) => api.get('/kbo/search', { params: { q, limit } }),
  enrichContacts: () => api.post('/kbo/enrich-contacts'),
};

// Interactions
export const interactionsAPI = {
  getAll: (params) => api.get('/interactions', { params }),
  create: (data) => api.post('/interactions', data),
  delete: (id) => api.delete(`/interactions/${id}`),
};

// Quotes
export const quotesAPI = {
  getAll: (params) => api.get('/quotes', { params: params ? { filter_status: params.status, contact_id: params.contact_id } : {} }),
  getOne: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post('/quotes', data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  delete: (id) => api.delete(`/quotes/${id}`),
  convert: (id) => api.post(`/quotes/${id}/convert`),
  getPdf: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, data) => api.post(`/quotes/${id}/send-email`, data),
  sendReminder: (id, data) => api.post(`/quotes/${id}/send-reminder`, data),
  createShareLink: (id) => api.post(`/quotes/${id}/share-link`),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params: params ? { filter_status: params.status, contact_id: params.contact_id } : {} }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  certifyPeppol: (id) => api.post(`/invoices/${id}/peppol/certify`),
  validatePeppol: (id) => api.get(`/invoices/${id}/peppol/validate`),
  exportPeppol: (id) => api.get(`/invoices/${id}/peppol/export`, { responseType: 'blob' }),
  getPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, data) => api.post(`/invoices/${id}/send-email`, data),
  sendReminder: (id, data) => api.post(`/invoices/${id}/send-reminder`, data),
};

// Email logs
export const emailLogsAPI = {
  getAll: (params) => api.get('/email-logs', { params }),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  // Multi-logo management
  listLogos: () => api.get('/settings/logos'),
  addLogo: (data) => api.post('/settings/logos', data),
  removeLogo: (id) => api.delete(`/settings/logos/${id}`),
  updateLogo: (id, data) => api.patch(`/settings/logos/${id}`, data),
};

// Price Library
export const priceLibraryAPI = {
  getAll: (params) => api.get('/price-library', { params }),
  getOne: (id) => api.get(`/price-library/${id}`),
  create: (data) => api.post('/price-library', data),
  update: (id, data) => api.put(`/price-library/${id}`, data),
  delete: (id) => api.delete(`/price-library/${id}`),
  getCategories: () => api.get('/price-library/categories'),
  importDefaults: () => api.post('/price-library/import-defaults'),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
  getPendingQuotes: (days = 5) => api.get('/dashboard/pending-quotes', { params: { days_threshold: days } }),
  runAutoReminders: () => api.post('/dashboard/run-auto-reminders'),
  runRecurringInvoices: () => api.post('/dashboard/run-recurring-invoices'),
  getUpcomingRecurrences: (limit = 10) => api.get('/dashboard/upcoming-recurrences', { params: { limit } }),
  getEmailFailures: (limit = 10) => api.get('/dashboard/email-failures', { params: { limit } }),
  resolveEmailFailure: (id) => api.post(`/dashboard/email-failures/${id}/resolve`),
  runFailureAlerts: () => api.post('/dashboard/run-failure-alerts'),
  getCashflowForecast: (months = 6, includeRec = true) => api.get('/dashboard/cashflow-forecast', { params: { months, include_recurrences: includeRec } }),
};

// Gmail OAuth
export const gmailAPI = {
  getLoginUrl: () => api.get('/oauth/gmail/login'),
  getStatus: () => api.get('/oauth/gmail/status'),
  disconnect: () => api.delete('/oauth/gmail/disconnect'),
};

// Microsoft OAuth (Outlook / Hotmail / Office 365)
export const microsoftAPI = {
  getLoginUrl: () => api.get('/oauth/microsoft/login'),
  getStatus: () => api.get('/oauth/microsoft/status'),
  disconnect: () => api.delete('/oauth/microsoft/disconnect'),
};

// SMTP direct
export const smtpAPI = {
  configure: (data) => api.post('/smtp/configure', data),
  getStatus: () => api.get('/smtp/status'),
  disconnect: () => api.delete('/smtp/disconnect'),
  detectPreset: (email) => api.post('/smtp/detect-preset', null, { params: { email } }),
};

// Email Templates
export const emailTemplatesAPI = {
  list: () => api.get('/email-templates'),
  update: (key, data) => api.put(`/email-templates/${key}`, data),
  reset: (key) => api.delete(`/email-templates/${key}`),
  sendTest: (key, data) => api.post(`/email-templates/${key}/send-test`, data),
};

// Team (multi-user / organization / permissions)
export const teamAPI = {
  me: () => api.get('/team/me'),
  organization: () => api.get('/team/organization'),
  updateOrganization: (data) => api.put('/team/organization', data),
  members: () => api.get('/team/members'),
  updateMember: (userId, data) => api.patch(`/team/members/${userId}`, data),
  removeMember: (userId) => api.delete(`/team/members/${userId}`),
  invitations: () => api.get('/team/invitations'),
  invitationsStats: () => api.get('/team/invitations/stats'),
  createInvitation: (data) => api.post('/team/invitations', data),
  revokeInvitation: (id) => api.delete(`/team/invitations/${id}`),
  resendInvitation: (id) => api.post(`/team/invitations/${id}/resend`),
  // Public (token-based)
  getInvitationByToken: (token) => api.get(`/team/invitations/by-token/${token}`),
  acceptInvitationLoggedIn: (token) => api.post(`/team/invitations/by-token/${token}/accept`),
  registerAndAccept: (token, data) => api.post(`/team/invitations/by-token/${token}/register`, data),
};

// Super-admin platform routes
export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  settings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  organizations: () => api.get('/admin/organizations'),
  updateOrganization: (id, data) => api.patch(`/admin/organizations/${id}`, data),
  revokeOrganization: (id, reason) => api.post(`/admin/organizations/${id}/revoke`, null, { params: { reason } }),
  restoreOrganization: (id) => api.post(`/admin/organizations/${id}/restore`),
  deleteOrganization: (id, purgeData = false) => api.delete(`/admin/organizations/${id}`, { params: { confirm: 'DELETE', purge_data: purgeData } }),
  recentInvitations: (limit = 25) => api.get('/admin/recent-invitations', { params: { limit } }),
};

// Tasks / Machines / Notifications
export const machinesAPI = {
  list: () => api.get('/machines'),
  create: (data) => api.post('/machines', data),
  update: (id, data) => api.patch(`/machines/${id}`, data),
  remove: (id) => api.delete(`/machines/${id}`),
};

export const tasksAPI = {
  list: (params = {}) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  remove: (id) => api.delete(`/tasks/${id}`),
  startTimer: (id) => api.post(`/tasks/${id}/timer/start`),
  stopTimer: (id) => api.post(`/tasks/${id}/timer/stop`),
  report: (params = {}) => api.get('/tasks/report', { params }),
  live: () => api.get('/tasks/live'),
  billableSummary: () => api.get('/tasks/billable-summary'),
  invoiceFromTasks: (data) => api.post('/tasks/invoice-from-tasks', data),
  addPhoto: (id, phase, dataUrl) => api.post(`/tasks/${id}/photos`, { phase, data_url: dataUrl }),
  removePhoto: (id, phase, index) => api.delete(`/tasks/${id}/photos/${phase}/${index}`),
  // Checklist
  addChecklist: (id, text) => api.post(`/tasks/${id}/checklist`, { text }),
  updateChecklist: (id, itemId, data) => api.patch(`/tasks/${id}/checklist/${itemId}`, data),
  removeChecklist: (id, itemId) => api.delete(`/tasks/${id}/checklist/${itemId}`),
  // Annotations (field notes)
  addAnnotation: (id, data) => api.post(`/tasks/${id}/annotations`, data),
  removeAnnotation: (id, annId) => api.delete(`/tasks/${id}/annotations/${annId}`),
  exportUrl: (params = {}) => {
    const search = new URLSearchParams(params).toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/tasks/report/export${search ? '?' + search : ''}`;
  },
};

export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
};

export const monthlyBillingAPI = {
  pending: () => api.get('/monthly-billing/pending'),
  getForContact: (contactId) => api.get(`/monthly-billing/contact/${contactId}`),
  addTask: (contactId, taskId) => api.post(`/monthly-billing/contact/${contactId}/add-task/${taskId}`),
  removeTask: (invoiceId, taskId) => api.delete(`/monthly-billing/${invoiceId}/task/${taskId}`),
  close: (invoiceId) => api.post(`/monthly-billing/${invoiceId}/close`),
  autoClosePrevious: () => api.post('/monthly-billing/auto-close-previous'),
};

export const meetingsAPI = {
  list: (params = {}) => api.get('/meetings', { params }),
  get: (id) => api.get(`/meetings/${id}`),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.patch(`/meetings/${id}`, data),
  remove: (id) => api.delete(`/meetings/${id}`),
  analyze: (text, titleHint) => api.post('/meetings/analyze', { text, title_hint: titleHint || null }),
  actionToTask: (id, actionId) => api.post(`/meetings/${id}/action-items/${actionId}/create-task`),
};

export const campaignsAPI = {
  list: () => api.get('/campaigns'),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  remove: (id) => api.delete(`/campaigns/${id}`),
  enroll: (id, contactIds) => api.post(`/campaigns/${id}/enroll`, { contact_ids: contactIds }),
  enrollments: (id) => api.get(`/campaigns/${id}/enrollments`),
  removeEnrollment: (id, enrId) => api.delete(`/campaigns/${id}/enrollments/${enrId}`),
  runNow: () => api.post('/campaigns/run-now'),
};

export default api;
